import {
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material'
import { Box } from '@mui/system'
import numeral from 'numeral'
import { BaseSyntheticEvent, FC, Fragment, useEffect } from 'react'
import { Button } from 'utoniq-ui'

import { MembershipInterval, PaymentMethod } from '@/clients/utoniq-core/schema'
import { useForm, UseFormProps, useRouter, useTranslation } from '@/core'

import { CreditCardCell } from '../../PaymentDetail/template'
import { Action, State } from '../viewModel'
import { RegisterPaymentMethodForm } from './RegisterPaymentMethodForm'

type ArtistMembershipPurchaseFormProps = {
  plans: State['plans']
  patron: State['patron']
  paymentSource?: State['paymentSource']
  submitting?: State['submitting']
  loading: boolean
  submittingCreatePayment: boolean
  onSubmit: Action['onSubmit']
  onSubmitDeletePayment: Action['onSubmitDeletePayment']
  onSubmitCreatePayment: Action['onSubmitCreatePayment']
}

export type ArtistMembershipPurchaseValues = {
  interval: MembershipInterval
}

export type ArtistMembershipPurchaseInput = ArtistMembershipPurchaseValues & {
  planId: string
}

export type MembershipPlan = {
  interval: MembershipInterval
  amount: number
  formattedAmount: string
  note?: string
  annualDiscountText?: string
  planId: string
}

const schema: UseFormProps['schema'] = o => ({
  interval: o.string(),
})

const defaultValues = {
  interval: MembershipInterval.Year,
}

export const ArtistMembershipPaymentEditForm: FC<ArtistMembershipPurchaseFormProps> =
  ({
    plans,
    patron,
    paymentSource,
    loading,
    submitting,
    onSubmit,
    onSubmitDeletePayment,
    onSubmitCreatePayment,
    submittingCreatePayment,
  }) => {
    const { t } = useTranslation('artistMembershipPaymentEdit')
    const { router, routerPages } = useRouter()

    const {
      handleSubmit,
      formState: { isSubmitting },
      watch,
      setValue,
      reset,
    } = useForm<ArtistMembershipPurchaseValues>({
      schema,
      defaultValues,
    })

    const intervalWatch = watch('interval')

    useEffect(() => {
      if (patron) {
        reset({
          interval:
            plans.find(plan => plan.planId === patron?.subscription?.planId)
              ?.interval ?? MembershipInterval.Year,
        })
      }
    }, [plans, patron])

    const handleOnSubmit = handleSubmit(async values => {
      await onSubmit(values)
    })

    return (
      <Stack>
        <Stack spacing={5} pb={5} pt={2}>
          <Typography
            variant="h5"
            sx={{ bgcolor: 'grey.50', px: 5, py: 2 }}
            color="text.secondary"
          >
            {t('course')}
          </Typography>
          <RadioGroup
            value={intervalWatch}
            onChange={(_, value) => {
              setValue('interval', value as never, {
                shouldValidate: true,
              })
            }}
            sx={{ gap: theme => theme.spacing(4), px: 5 }}
          >
            {plans.map((plan, index) => {
              // NOTE: 年間 → 月間への変更APIは使用できないため、月額への切り替え導線を非表示にする
              // https://app.clickup.com/t/908127/UT-15353
              if (plan.interval === MembershipInterval.Month) {
                return null
              }

              return (
                <Fragment key={plan.interval}>
                  <FormControlLabel
                    value={plan.interval}
                    checked={intervalWatch === plan.interval}
                    control={<Radio />}
                    label={
                      <Stack sx={{ gap: 1 }}>
                        <Typography variant="h5">
                          {t(`${plan.interval}Course`)}
                        </Typography>
                        <Stack direction="row" alignItems="center">
                          <Typography variant="subtitle1">
                            {t(`${plan.interval}CoursePrice`, {
                              amount: plan.formattedAmount,
                            })}
                          </Typography>
                          {plan.interval === MembershipInterval.Year && (
                            <Typography variant="body2">
                              (
                              {t('monthCoursePrice', {
                                amount: numeral(
                                  Math.ceil(plan.amount / 12)
                                ).format('0,0'),
                              })}
                              )
                            </Typography>
                          )}
                          {plan.interval === MembershipInterval.Year &&
                            !!plan.annualDiscountText && (
                              <Typography
                                variant="subtitle2"
                                color="primary.main"
                                sx={{ ml: 1 }}
                              >
                                {plan.annualDiscountText}
                              </Typography>
                            )}
                        </Stack>
                      </Stack>
                    }
                  />
                  {plans.length - 1 > index && <Divider />}
                </Fragment>
              )
            })}
          </RadioGroup>
        </Stack>
        <Stack spacing={5} pb={5}>
          <Typography
            variant="h5"
            sx={{ bgcolor: 'grey.50', px: 5, py: 2 }}
            color="text.secondary"
          >
            {t('method', '支払い方法の変更')}
          </Typography>
        </Stack>
        <Stack sx={{ px: 5, pb: 5 }} spacing={2}>
          <Typography variant="subtitle2" color="text.secondary">
            {t('currentMethod', '現在の支払い方法')}
          </Typography>
          <Typography variant="subtitle1">
            {patron?.paymentMethod === PaymentMethod.CreditCard
              ? t('creditCard', 'クレジットカード')
              : t('convenienceStore', 'コンビニ決済')}
          </Typography>
          <Box sx={{ height: '1px', bgcolor: 'grey.100' }} />
        </Stack>
        {paymentSource ? (
          // クレカ登録済みの場合
          <RegisteredCreditCardCell
            loading={loading}
            paymentSource={paymentSource}
            onSubmitDeletePayment={onSubmitDeletePayment}
            isSubmitting={isSubmitting || !!submitting}
            handleOnSubmit={handleOnSubmit}
          />
        ) : (
          // クレカ未登録の場合
          <RegisterPaymentMethodForm
            onSubmitCreatePayment={onSubmitCreatePayment}
            submitting={submittingCreatePayment}
            loading={loading}
          />
        )}
        <Stack sx={{ my: 5 }} alignItems="center" gap={4}>
          <Button
            variant="text"
            size="small"
            sx={{ width: 212 }}
            onClick={() => router.goBack()}
          >
            {t('back', '戻る')}
          </Button>
        </Stack>
      </Stack>
    )
  }

const RegisteredCreditCardCell = ({
  loading,
  paymentSource,
  onSubmitDeletePayment,
  isSubmitting,
  handleOnSubmit,
}: {
  loading: boolean
  isSubmitting: boolean
  paymentSource: State['paymentSource']
  onSubmitDeletePayment: Action['onSubmitDeletePayment']
  handleOnSubmit: (e?: BaseSyntheticEvent) => Promise<void>
}) => {
  const { t } = useTranslation('artistMembershipPaymentEdit')
  if (!paymentSource) <></>

  return (
    <Stack sx={{ px: 5 }} spacing={2}>
      <Typography variant="subtitle2">
        {t('changeMethod', '下記のクレジットカードに支払い方法を変更します。')}
      </Typography>
      <CreditCardCell
        cardNumber={paymentSource?.last4}
        name={paymentSource?.name ?? ''}
        visible={loading}
        onDeletePaymentSource={onSubmitDeletePayment}
        brand={paymentSource?.brand}
        expirationDate={`${paymentSource?.expMonth}/${paymentSource?.expYear}`}
      />
      <Stack
        sx={{
          pt: 4,
          alignItems: 'center',
        }}
      >
        <Button
          size="large"
          disabled={isSubmitting}
          loading={isSubmitting}
          sx={{ width: 212 }}
          onClick={handleOnSubmit}
        >
          {t('submit', '変更する')}
        </Button>
      </Stack>
    </Stack>
  )
}
